import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  RENDER_EXPORT_GUIDE, RENDER_FBX_KEEPS, RENDER_FBX_LOSES, RenderExportApp, RenderExporter, RenderGuideApp, renderGuideApp,
} from '../render-export-guide';
import { canInflate } from '../render-zip';
import { RENDER_SUPPORT_EMAIL, renderFaq, RenderFaqItem } from '../render.data';

export type RenderHelpTab = 'guide' | 'faq';

export interface RenderHelpOptions {
  tab?: RenderHelpTab;
  app?: RenderExportApp;
  // A step of the tab to scroll to and ring for a moment (0-based), or 'pack': the one that packs
  // the textures in ("Show me how" under the texture checklist).
  step?: number | 'pack';
  // The program read out of the user's FBX ("Exported from Blender 2.76"); opens its tab.
  exporter?: RenderExporter;
}

// The tab picked last, so the next opening starts there (this browser only).
const APP_KEY = '3dway.render-help.app';
const SPOTLIGHT_MS = 2400;

// Help (owner, 2026-09-30, the export-guide proposals): a modal over any page of the flow — the
// zone's "How to include textures", a refused drop, "Show me how" under the texture checklist,
// the preview of a model without textures, the error of one whose textures could not be read,
// and the landing's FAQ. Two tabs: the designer's export instruction, one tab per program
// (render-export-guide.ts), and the FAQ, the texture questions first. The body scrolls, the
// head and the foot stay.
@Component({
  selector: 'app-render-help-dialog',
  templateUrl: './render-help-dialog.component.html',
  styleUrls: ['./render-help-dialog.component.scss'],
})
export class RenderHelpDialogComponent implements AfterViewInit, OnDestroy {
  public readonly apps = RENDER_EXPORT_GUIDE;
  public readonly keeps = RENDER_FBX_KEEPS;
  public readonly loses = RENDER_FBX_LOSES;
  public readonly faq: RenderFaqItem[];
  public readonly supportEmail = RENDER_SUPPORT_EMAIL;
  public readonly exporter?: RenderExporter;

  public tab: RenderHelpTab;
  public app: RenderGuideApp;
  public spotlight: number | null;

  @ViewChild('body') private body?: ElementRef<HTMLElement>;
  @ViewChildren('stepItem') private stepItems?: QueryList<ElementRef<HTMLElement>>;

  private spotlightTimer?: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: RenderHelpOptions | null,
    private readonly dialogRef: MatDialogRef<RenderHelpDialogComponent>,
  ) {
    const options = data || {};
    this.tab = options.tab || 'guide';
    this.exporter = options.exporter;
    this.app = renderGuideApp(options.app || options.exporter?.app || storedApp() || 'blender');
    this.spotlight = (options.step === 'pack' ? this.app.packStep : options.step) ?? null;
    const faq = renderFaq(canInflate());
    this.faq = [...faq.filter((item) => item.textures), ...faq.filter((item) => !item.textures)];
  }

  ngAfterViewInit(): void {
    const index = this.spotlight;
    if (index === null) {
      return;
    }
    // After the dialog's first frame, when the steps have their places.
    window.setTimeout(() => {
      const body = this.body?.nativeElement;
      const step = this.stepItems?.get(index)?.nativeElement;
      if (body && step) {
        body.scrollTop = Math.max(0, step.offsetTop - 20);
      }
    });
    this.spotlightTimer = window.setTimeout(() => (this.spotlight = null), SPOTLIGHT_MS);
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.spotlightTimer);
  }

  public selectTab(tab: RenderHelpTab): void {
    this.tab = tab;
    this.toTop();
  }

  public selectApp(app: RenderGuideApp): void {
    this.app = app;
    this.spotlight = null;
    try {
      localStorage.setItem(APP_KEY, app.id);
    } catch {
      // Storage off (a private window): the next opening starts at Blender again.
    }
    this.toTop();
  }

  // Arrow keys along a row of tabs, as a tablist does.
  public onTabsKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    const tabs = Array.from((event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role="tab"]'));
    const at = tabs.indexOf(document.activeElement as HTMLElement);
    const next = tabs[(at + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    if (next) {
      event.preventDefault();
      next.focus();
      next.click();
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public trackByApp(index: number, app: RenderGuideApp): string {
    return app.id;
  }

  private toTop(): void {
    const body = this.body?.nativeElement;
    if (body) {
      body.scrollTop = 0;
    }
  }
}

function storedApp(): RenderExportApp | null {
  try {
    const id = localStorage.getItem(APP_KEY);
    return RENDER_EXPORT_GUIDE.some((app) => app.id === id) ? (id as RenderExportApp) : null;
  } catch {
    return null;
  }
}
